import axios from "axios";
import Cookies from "universal-cookie";
import jwtDecode from "jwt-decode";
import { storeUserData } from "../../redux/reducers/userDataSlice";
import { Store } from "../../redux/store/store";

const REFRESH_PATH = "/api/Auth/RefreshToken";

let refreshInFlight: Promise<RefreshAccessTokenResult> | null = null;

export type RefreshAccessTokenResult =
  | { success: true; accessToken: string }
  | { success: false; shouldLogout: boolean };

function gatewayBaseUrl(): string {
  const base = process.env.REACT_APP_URL?.replace(/\/$/, "") ?? "";
  return base;
}

/** Match API keys case-insensitive (e.g. refreshtoken vs refreshToken vs RefreshToken). */
function normalizeJsonKey(key: string): string {
  return key.toLowerCase().replace(/_/g, "");
}

function stringFieldsByNormalizedKey(obj: Record<string, unknown>): Map<string, string> {
  const map = new Map<string, string>();
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === "string" && v.length > 0) {
      map.set(normalizeJsonKey(k), v);
    }
  }
  return map;
}

/** Access JWT: prefer accessToken, then token (refresh APIs often return only `token`). */
function readAccessTokenFromObject(obj: Record<string, unknown>): string {
  const m = stringFieldsByNormalizedKey(obj);
  return m.get("accesstoken") || m.get("token") || "";
}

/** Refresh value: API may send `refreshtoken` (one word) or camelCase `refreshToken`. */
function readRefreshTokenFromObject(obj: Record<string, unknown>): string {
  const m = stringFieldsByNormalizedKey(obj);
  return m.get("refreshtoken") || "";
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v !== null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

/** Collect nested objects .NET / JS APIs use for token payloads. */
function collectTokenPayloadCandidates(root: Record<string, unknown>): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [root];
  const pushIfObject = (v: unknown) => {
    const r = asRecord(v);
    if (r) out.push(r);
  };
  pushIfObject(root.data);
  pushIfObject(root.Data);
  pushIfObject(root.result);
  pushIfObject(root.Result);
  pushIfObject(root.value);
  pushIfObject(root.Value);
  const mid = asRecord(root.data ?? root.Data);
  if (mid) {
    out.push(mid);
    pushIfObject(mid.data);
    pushIfObject(mid.Data);
  }
  return out;
}

function unwrapJsonBody(data: unknown): unknown {
  if (typeof data === "string") {
    const t = data.trim();
    if (!t) return data;
    try {
      return JSON.parse(t);
    } catch {
      return data;
    }
  }
  return data;
}

/** Depth-first search for an object that contains both access + refresh strings. */
function findNestedTokenPayload(node: unknown, depth: number): Record<string, unknown> | null {
  if (depth > 8 || node === null) return null;
  if (typeof node !== "object") return null;
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findNestedTokenPayload(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  const obj = node as Record<string, unknown>;
  const access = readAccessTokenFromObject(obj);
  const refresh = readRefreshTokenFromObject(obj);
  if (access && refresh) return obj;
  for (const v of Object.values(obj)) {
    const found = findNestedTokenPayload(v, depth + 1);
    if (found) return found;
  }
  return null;
}

function parseRefreshResponse(data: unknown): { accessToken: string; refreshToken: string } | null {
  const unwrapped = unwrapJsonBody(data);
  const root = asRecord(unwrapped);
  if (!root) return null;

  for (const obj of collectTokenPayloadCandidates(root)) {
    const accessToken = readAccessTokenFromObject(obj);
    const refreshToken = readRefreshTokenFromObject(obj);
    if (accessToken && refreshToken) {
      return { accessToken, refreshToken };
    }
  }

  const nested = findNestedTokenPayload(unwrapped, 0);
  if (nested) {
    const accessToken = readAccessTokenFromObject(nested);
    const refreshToken = readRefreshTokenFromObject(nested);
    if (accessToken && refreshToken) {
      return { accessToken, refreshToken };
    }
  }
  return null;
}

export function persistRefreshedTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem("AccessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  const raw = localStorage.getItem("loginData");
  if (raw) {
    try {
      const loginData = JSON.parse(raw) as Record<string, unknown>;
      loginData.accessToken = accessToken;
      loginData.refreshToken = refreshToken;
      localStorage.setItem("loginData", JSON.stringify(loginData));
    } catch {
      /* ignore corrupt loginData */
    }
  } else {
    const current = Store.getState().userDataSlice?.loginData;
    if (current && typeof current === "object") {
      const merged = { ...current, accessToken, refreshToken };
      localStorage.setItem("loginData", JSON.stringify(merged));
    }
  }

  const updatedRaw = localStorage.getItem("loginData");
  if (updatedRaw) {
    try {
      const loginData = JSON.parse(updatedRaw);
      Store.dispatch(storeUserData({ loginData }));
    } catch {
      /* ignore */
    }
  }

  try {
    const decoded = jwtDecode(accessToken) as {
      stsUserId?: string;
      role?: string;
    };
    const cookies = new Cookies();
    const date = new Date();
    date.setTime(date.getTime() + 3 * 60 * 60 * 1000);
    cookies.set(
      "SecureToken",
      {
        UserID: decoded.stsUserId,
        accesstoken: accessToken,
        role: decoded.role,
      },
      { path: "/", expires: date, sameSite: "strict" }
    );
  } catch {
    /* cookie update is best-effort */
  }
}

/**
 * Single-flight refresh against the gateway.
 * `shouldLogout` is true when the refresh endpoint fails for any reason (HTTP error, invalid body, network).
 */
export function refreshAccessToken(): Promise<RefreshAccessTokenResult> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const attempt = (async (): Promise<RefreshAccessTokenResult> => {
    try {
      const token = localStorage.getItem("AccessToken");
      const refreshToken = localStorage.getItem("refreshToken");
      if (!token || !refreshToken) {
        return { success: false, shouldLogout: true };
      }

      const base = gatewayBaseUrl();
      if (!base) {
        return { success: false, shouldLogout: true };
      }

      const { data, status } = await axios.post(
        `${base}${REFRESH_PATH}`,
        { token, refreshToken },
        {
          headers: { "Content-Type": "application/json" },
          validateStatus: (s) => s < 500,
        }
      );
      if (status < 200 || status >= 300) {
        return { success: false, shouldLogout: true };
      }

      const parsed = parseRefreshResponse(data);
      if (!parsed) {
        return { success: false, shouldLogout: true };
      }

      persistRefreshedTokens(parsed.accessToken, parsed.refreshToken);
      return { success: true, accessToken: parsed.accessToken };
    } catch {
      return { success: false, shouldLogout: true };
    }
  })();

  refreshInFlight = attempt.finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}
